import { Suspense, useCallback, useEffect, useState } from 'react';
import { CREATOR, DEFAULT_CONNECTIONS, setCreatorIdentity } from './data/seed';
import { api } from './lib/api';
import { lazyNamed, preloadWhenIdle } from './lib/lazyNamed';
import { StoreContext, useDebouncedSaver } from './lib/storeContext';
import { useTheme } from './lib/theme';
// The landing page is what most visitors see first, so it ships in the main
// bundle instead of costing an extra round trip before anything appears.
import { LandingPage } from './pages/Landing/LandingPage';

// Every other page is its own chunk, downloaded the first time it's shown.
const AuthGate = lazyNamed(() => import('./pages/Auth/AuthGate'), 'AuthGate');
const Storefront = lazyNamed(() => import('./pages/Storefront/Storefront'), 'Storefront');
const Studio = lazyNamed(() => import('./pages/Studio/Studio'), 'Studio');
const OnboardingWizard = lazyNamed(() => import('./pages/Studio/onboarding/OnboardingWizard'), 'OnboardingWizard');

/* ============================================================================
   ROOT APP
   ============================================================================ */

// Storefront shown to visitors who aren't signed in (the canonical demo store)
const PUBLIC_STORE_HANDLE = 'novasays';

// Postgres jsonb doesn't keep key order; the UI lists connections in the
// designed order (payments, branding, email, …), so restore it on load.
const orderConnections = c => {
  const known = Object.keys(DEFAULT_CONNECTIONS).filter(k => c[k]);
  const extra = Object.keys(c).filter(k => !(k in DEFAULT_CONNECTIONS));
  return Object.fromEntries([...known, ...extra].map(k => [k, c[k]]));
};
const rid = prefix => prefix + '_' + Math.random().toString(36).slice(2, 10);

// The platform admin console (separate skrl-admin repo)
const ADMIN_URL = /^(localhost|127\.)/.test(window.location.hostname) ? 'http://localhost:3200' : 'https://admin.skrl.net';

export default function App() {
  const [view, setView] = useState('landing'); // 'landing' | 'customer' | 'studio'
  // Only the marketing site has URLs · the store and studio live at /
  useEffect(() => {
    if (view !== 'landing' && window.location.pathname !== '/') window.history.replaceState(null, '', '/');
  }, [view]);
  const [authed, setAuthed] = useState(false);
  const [storeReturn, setStoreReturn] = useState('landing'); // where the storefront's back button goes
  const [authInitialTab, setAuthInitialTab] = useState('login'); // 'login' | 'register'
  const [user, setUser] = useState(null);
  const [storeHandle, setStoreHandle] = useState(PUBLIC_STORE_HANDLE);
  const [prefs, setPrefs] = useState({});
  const [connections, setConnections] = useState({});
  const [videos, setVideos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [pages, setPages] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  // The live demo opens on the creator's real home feed (videos + products)
  const [initialPageId, setInitialPageId] = useState('home');
  const [onboarding, setOnboarding] = useState(false);
  const [credits, setCredits] = useState(0);
  const [generations, setGenerations] = useState([]); // past AI generations
  const [customAvatars, setCustomAvatars] = useState([]); // user-trained avatars
  const [siteSettings, setSiteSettings] = useState({});
  const [notice, setNotice] = useState(null); // { text, tone } · save errors etc.
  const [themeMode, setThemeMode] = useTheme();
  const saveLater = useDebouncedSaver(500);

  // ── Loading data ───────────────────────────────────────────────────────────
  // Signed-in creator · everything from one bootstrap response
  const hydrate = useCallback(d => {
    setCreatorIdentity({ name: d.user.name, handle: d.store.handle, domain: d.store.domain, avatar: d.store.settings?.avatar, bio: d.user.bio });
    setUser(d.user);
    setStoreHandle(d.store.handle);
    setPrefs(d.store.prefs || {});
    setConnections(orderConnections(d.store.connections || {}));
    setSiteSettings(d.store.settings || {});
    setCredits(d.store.credits ?? 0);
    setVideos(d.videos || []);
    setCustomers(d.customers || []);
    setSubscribers(d.subscribers || []);
    setPages(d.pages || []);
    setOrders(d.orders || []);
    setGenerations(d.generations || []);
    setCustomAvatars(d.avatars || []);
  }, []);

  // Visitors see the public demo storefront
  const loadPublic = useCallback(async handle => {
    const d = await api.publicStore(handle);
    setCreatorIdentity({ name: d.store.name, handle: d.store.handle, domain: d.store.domain, avatar: d.store.settings?.avatar });
    setStoreHandle(d.store.handle);
    setSiteSettings(d.store.settings || {});
    setVideos(d.videos);
    setPages(d.pages);
  }, []);

  // Re-read the server's copy (after a failed save, or when another action changed several lists)
  const resync = useCallback(() => api.store().then(hydrate).catch(() => {}), [hydrate]);

  const showNotice = useCallback((text, tone = 'error') => {
    setNotice({ text, tone });
    setTimeout(() => setNotice(n => (n && n.text === text ? null : n)), 4500);
  }, []);

  // Optimistic update already applied · if the server rejects it, reload its copy
  const persist = useCallback(promise => promise.catch(err => {
    console.error(err);
    showNotice("Couldn't save that change · reloaded your latest data");
    resync();
  }), [resync, showNotice]);

  useEffect(() => {
    let alive = true;
    api.me()
      .then(d => {
        if (!alive) return;
        if (d.user && d.store) { hydrate(d); setAuthed(true); return; }
        return loadPublic(PUBLIC_STORE_HANDLE);
      })
      .catch(() => alive && showNotice("Can't reach the server · is the API running?"));
    return () => { alive = false; };
  }, [hydrate, loadPublic, showNotice]);

  // ── Site settings · instant locally, saved after typing pauses ─────────────
  const updateSiteSetting = (key, value) => {
    setSiteSettings(prev => ({ ...prev, [key]: value }));
    if (authed) saveLater('setting:' + key, () => persist(api.settings({ [key]: value })));
  };

  // ── Storefront ─────────────────────────────────────────────────────────────
  // Prices, order number, customer and stats are all computed by the server.
  const recordPurchase = (email, items, _total, pageId) => {
    api.checkout(storeHandle, { email, items, pageId })
      .then(() => { if (authed) resync(); })
      .catch(err => showNotice(err.code === 'out_of_stock' ? err.message : "Checkout didn't go through · please try again"));
  };

  const bumpStat = (videoId, key, by = 1) => {
    setVideos(vs => vs.map(v =>
      v.id === videoId ? { ...v, stats: { ...v.stats, [key]: (v.stats?.[key] || 0) + by } } : v
    ));
    api.track(storeHandle, { kind: 'video', id: videoId, key, by });
  };

  const bumpPageView = (pageId) => {
    setPages(ps => ps.map(p => p.id === pageId ? { ...p, stats: { ...p.stats, views: (p.stats?.views || 0) + 1 } } : p));
    api.track(storeHandle, { kind: 'page', id: pageId });
  };

  // ── Studio data ────────────────────────────────────────────────────────────
  const updateOrder = (id, patch) => {
    setOrders(os => os.map(o => (o.id === id ? { ...o, ...patch } : o)));
    // The server appends the timeline entry and logs refunds
    persist(api.patch('orders', id, patch).then(({ item }) => setOrders(os => os.map(o => (o.id === id ? item : o)))));
  };

  const addVideo = (v) => {
    const video = v.id ? v : { ...v, id: rid('v') };
    setVideos(vs => [video, ...vs]);
    persist(api.create('videos', video));
  };

  // New product (Products → New product) · products live on feed videos
  const addProduct = (product) => addVideo({
    id: rid('v'),
    caption: product.title,
    bg: 'radial-gradient(ellipse at 30% 20%, #ffb4c6 0%, #c4a1ff 40%, #4f3cd9 100%)',
    product,
    stats: { views: 0, taps: 0, addToCart: 0, purchases: 0 },
    posted: 'just now',
  });

  const addPage = (p) => {
    setPages(ps => [...ps, p]);
    persist(api.create('pages', p));
  };

  const reorderPages = (fromId, toId) => {
    if (fromId === toId) return;
    const fromIdx = pages.findIndex(p => p.id === fromId);
    const toIdx = pages.findIndex(p => p.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...pages];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setPages(next);
    persist(api.reorderPages(next.map(p => p.id)));
  };

  const updatePage = (id, patch) => {
    setPages(ps => ps.map(p => p.id === id ? { ...p, ...patch } : p));
    persist(api.patch('pages', id, patch));
  };

  const deletePage = (id) => {
    setPages(ps => ps.filter(p => p.id !== id));
    persist(api.remove('pages', id));
  };

  // Record a completed AI generation · the server deducts credits.
  // Does NOT publish to the main feed · that's a separate step.
  // AI generation · the server charges credits and runs the job (fal.ai or simulated).
  // Resolves with the saved generation; rejects with the API error.
  const recordGeneration = async (gen, image) => {
    const r = await api.generation(gen, image);
    setGenerations(gs => [r.item, ...gs]);
    setCredits(r.credits);
    if (r.item.status === 'failed') showNotice(`Generation failed · credits refunded · ${r.item.error || 'try again'}`);
    return r.item;
  };

  const pendingGenIds = generations.filter(g => g.status === 'queued' || g.status === 'running').map(g => g.id).join(',');
  useEffect(() => {
    if (!pendingGenIds) return;
    const t = setInterval(() => {
      pendingGenIds.split(',').forEach(id => api.getGeneration(id).then(r => {
        setGenerations(gs => gs.map(g => (g.id === id ? r.item : g)));
        setCredits(r.credits);
        if (r.item.status === 'failed') showNotice(`Generation failed · credits refunded · ${r.item.error || 'try again'}`);
      }).catch(() => {}));
    }, 4000);
    return () => clearInterval(t);
  }, [pendingGenIds, showNotice]);

  // Trained avatar · training progress is simulated here, the result is saved
  const addCustomAvatar = (avatar) => {
    const created = { ...avatar, id: avatar.id || rid('av'), status: 'training', progress: 0, createdAt: Date.now() };
    setCustomAvatars(a => [created, ...a]);
    persist(api.create('avatars', created));

    const totalMs = 8000;
    const tickMs = 250;
    let elapsed = 0;
    const tick = () => {
      elapsed += tickMs;
      const pct = Math.min(100, Math.round((elapsed / totalMs) * 100));
      setCustomAvatars(list => list.map(a =>
        a.id === created.id ? { ...a, progress: pct, status: pct >= 100 ? 'ready' : 'training' } : a
      ));
      if (pct < 100) setTimeout(tick, tickMs);
      else persist(api.patch('avatars', created.id, { status: 'ready', progress: 100 }));
    };
    setTimeout(tick, tickMs);
    return created.id;
  };

  // Publish an AI generation as a real video in the feed.
  // Publish · the finished video becomes the product video's footage
  const publishGeneration = (gen, videoId) => {
    if (!gen.videoUrl || !videoId) return;
    const patch = { videoUrl: gen.videoUrl, aiGenerated: true, generationId: gen.id };
    setVideos(vs => vs.map(v => (v.id === videoId ? { ...v, ...patch } : v)));
    persist(api.patch('videos', videoId, patch));
    showNotice('Video published to your store', 'success');
  };

  const viewPage = (pageId) => {
    setInitialPageId(pageId);
    setStoreReturn('studio');
    setView('customer');
  };

  const updateConnection = (id, data) => {
    setConnections(c => ({ ...c, [id]: { ...c[id], ...data } }));
    persist(api.connection(id, data));
  };

  // ── Account ────────────────────────────────────────────────────────────────
  const setPref = useCallback((key, value) => {
    setPrefs(p => ({ ...p, [key]: value }));
    saveLater('pref:' + key, () => persist(api.pref(key, value)));
  }, [saveLater, persist]);

  const changeTier = async (tier) => {
    setUser(u => ({ ...u, tier }));
    const r = await api.tier(tier);
    setUser(u => ({ ...u, ...r.user }));
    setCredits(r.store.credits);
    return r;
  };

  const storeActions = {
    user,
    prefs,
    setPref,
    updateProfile: async patch => {
      const r = await api.updateMe(patch);
      setCreatorIdentity({ name: r.user.name, handle: r.user.handle, domain: CREATOR.domain.startsWith('skrl.net/') ? null : CREATOR.domain, bio: r.user.bio });
      setUser(u => ({ ...u, ...r.user }));
      if (patch.handle) setStoreHandle(r.user.handle);
      return r.user;
    },
    changePassword: (current, next) => api.changePassword(current, next),
    changeTier,
    cancelPlan: async () => {
      const r = await api.cancelPlan();
      setUser(u => ({ ...u, ...r.user }));
      return r;
    },
    openTicket: (subject, priority) => api.ticket(subject, priority),
  };

  // Sign-in / sign-up forms call the API and hand us the bootstrap response
  const login = (d) => {
    if (!d.store) {
      // Platform admins have no store · their console is a separate app (the skrl-admin repo)
      api.logout().catch(() => {});
      if (d.user?.role === 'admin') window.location.assign(ADMIN_URL);
      else setView('landing');
      return;
    }
    hydrate(d);
    setAuthed(true);
    setOnboarding(false);
  };
  const register = (d) => {
    hydrate(d);
    setAuthed(true);
    setOnboarding(true);
  };
  const logout = () => {
    api.logout().catch(() => {}).finally(() => {
      setAuthed(false);
      setOnboarding(false);
      setUser(null);
      setOrders([]); setCustomers([]); setSubscribers([]); setGenerations([]); setCustomAvatars([]);
      setConnections({}); setPrefs({}); setCredits(0); setCart([]);
      setView('landing');
      loadPublic(PUBLIC_STORE_HANDLE).catch(() => {});
    });
  };

  const goToStudioAuth = (tab) => {
    setAuthInitialTab(tab || 'login');
    setView('studio');
  };

  // Fetch the main pages in the background so switching views stays instant.
  useEffect(() => preloadWhenIdle(Storefront, AuthGate, Studio), []);

  return (
    <div
      className="font-body"
      style={{ backgroundColor: 'var(--bg)', color: 'var(--fg)', minHeight: '100vh' }}
    >

      {/* ViewToggle removed · landing nav has its own theme toggle */}

      <Suspense fallback={null}>
        {view === 'landing' && (
          <LandingPage
            onStartFree={() => goToStudioAuth('register')}
            onSignIn={() => goToStudioAuth('login')}
            onViewStore={() => { setStoreReturn('landing'); setInitialPageId('home'); setView('customer'); }}
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            pages={pages}
            videos={videos}
            siteSettings={siteSettings}
          />
        )}

        {view === 'customer' && (
          <Storefront
            videos={videos}
            pages={pages}
            initialPageId={initialPageId}
            cart={cart} setCart={setCart}
            onCheckout={recordPurchase}
            onBumpStat={bumpStat}
            onBumpPageView={bumpPageView}
            siteSettings={siteSettings}
            onExit={() => setView(storeReturn === 'studio' && authed ? 'studio' : 'landing')}
            exitLabel={storeReturn === 'studio' && authed ? 'Back to studio' : 'Back to skrl'}
          />
        )}

        {view === 'studio' && (authed && user ? (
          <StoreContext.Provider value={storeActions}>
            <Studio
              themeMode={themeMode}
              setThemeMode={setThemeMode}
              videos={videos}
              customers={customers}
              subscribers={subscribers}
              orders={orders}
              pages={pages}
              user={user}
              connections={connections}
              credits={credits}
              generations={generations}
              onAddVideo={addVideo}
              onCreateProduct={addProduct}
              onAddPage={addPage}
              onReorderPages={reorderPages}
              onUpdatePage={updatePage}
              onDeletePage={deletePage}
              onViewPage={viewPage}
              onUpdateConnection={updateConnection}
              onUpdateOrder={updateOrder}
              onRecordGeneration={recordGeneration}
              onPublishGeneration={publishGeneration}
              customAvatars={customAvatars}
              onCreateAvatar={addCustomAvatar}
              onLogout={logout}
              onGoLanding={() => setView('landing')}
              siteSettings={siteSettings}
              onUpdateSiteSetting={updateSiteSetting}
            />
            {onboarding && (
              <OnboardingWizard
                user={user}
                connections={connections}
                onUpdateConnection={updateConnection}
                onApplyTemplate={(template) => {
                  // Apply the picked template's blocks to the home page
                  // Remap block + overlay IDs so re-applying works without collisions
                  const home = pages.find(p => p.type === 'home') || pages[0];
                  if (!home) return;
                  const blocksWithFreshIds = template.blocks.map(b => {
                    const props = { ...b.props };
                    if (props.overlays) props.overlays = props.overlays.map(o => ({ ...o, id: rid('ov') }));
                    return { ...b, id: rid('b'), props };
                  });
                  updatePage(home.id, { blocks: blocksWithFreshIds, background: template.background || home.background });
                }}
                onSelectTier={(tier) => {
                  // Commit the plan · the server also tops credits up to the plan's allotment
                  changeTier(tier).catch(() => showNotice("Couldn't change your plan · please try again"));
                }}
                onClose={() => setOnboarding(false)}
              />
            )}
          </StoreContext.Provider>
        ) : (
          <AuthGate
            initialTab={authInitialTab}
            onLogin={login}
            onRegister={register}
            onBackToStore={() => setView('landing')}
          />
        ))}
      </Suspense>

      {notice && (
        <div role="status" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[100] px-4 py-2.5 rounded-xl text-[13px] font-medium shadow-lg"
             style={{ backgroundColor: notice.tone === 'error' ? '#7f1d1d' : '#141412', color: '#fff', maxWidth: 'calc(100vw - 32px)' }}>
          {notice.text}
        </div>
      )}
    </div>
  );
}
